/**
 * EstimatesUploadPage.tsx
 * Super Admin-only page for uploading an Estimates Excel file.
 * Data is parsed server-side and upserted into the EF_Exclusion table.
 */

import { useState } from 'react';
import { Upload, Progress, Alert, Card, Button, Table } from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import { NSPButton } from '@components/ui/NSPButton';
import { useEstimatesUpload, type SkippedRow } from '@hooks/useEstimatesUpload';
import styles from './EstimatesUploadPage.module.css';
import { withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

// ── Progress helpers ─────────────────────────────────────────────────────────

type ProgressStatus = 'idle' | 'active' | 'completed' | 'failed';

function toAntdStatus(s: ProgressStatus): 'active' | 'success' | 'exception' | 'normal' {
  if (s === 'active') return 'active';
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'exception';
  return 'normal';
}

// ── Skipped rows table columns ───────────────────────────────────────────────

const skippedColumns: ColumnsType<SkippedRow> = [
  { title: 'Row', dataIndex: 'rowNumber', width: 80, align: 'right' },
  { title: 'Meter Number', dataIndex: 'meterNumber', render: (v: string | null) => v ?? '—' },
  { title: 'Reason', dataIndex: 'reason' },
];

// ── Component ────────────────────────────────────────────────────────────────

export default function EstimatesUploadPage() {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);

  const { uploading, result, error, uploadFile, reset } = useEstimatesUpload();

  // ── File list handler ──────────────────────────────────────────────
  const handleChange = ({ fileList: newList }: { fileList: UploadFile[] }) => {
    setFileList(newList.slice(-1));
    if (result || error) reset();
    if (progressStatus !== 'idle') {
      setProgressStatus('idle');
      setProgressPercent(0);
      setProgressMessage(null);
    }
  };

  const handleCloseProgress = () => {
    setProgressStatus('idle');
    setProgressPercent(0);
    setProgressMessage(null);
  };

  // ── Upload handler ─────────────────────────────────────────────────
  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file) return;

    setProgressStatus('active');
    setProgressPercent(20);
    setProgressMessage('Uploading file...');

    try {
      setProgressPercent(50);
      setProgressMessage('Processing estimates data...');

      const ok = await uploadFile(file);

      if (ok) {
        setProgressStatus('completed');
        setProgressPercent(100);
        setProgressMessage('Upload completed');
        setFileList([]);
      } else {
        setProgressStatus('failed');
        setProgressPercent(100);
        setProgressMessage('Upload failed');
      }
    } catch {
      setProgressStatus('failed');
      setProgressPercent(100);
      setProgressMessage('Upload failed');
    }
  };

  // ── Derived state ──────────────────────────────────────────────────
  const isBusy = uploading;
  const showProgress = progressStatus !== 'idle';
  const canClose = progressStatus === 'completed' || progressStatus === 'failed';
  const uploadData = result?.success ? result.data : null;

  return (
    <div className={styles.page} data-testid="estimates-upload-page">
      {/* ── Page heading ──────────────────────────────────────────── */}
      <div className={styles.pageTitle}>Upload Estimates</div>
      <div className={styles.pageSubtitle}>
        Upload an Estimates Excel file (.xlsx / .xls / .xlsm) to import exclusion data
        into the EF_Exclusion table. Existing records for the same month-year will be replaced.
      </div>

      {/* ── Upload panel ──────────────────────────────────────────── */}
      <div className={styles.uploadPanel}>
        <div className={styles.panelTitle}>Upload Estimates File</div>

        {/* Error alert */}
        {error && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={reset}
            className={styles.alert}
            message="Upload Failed"
            description={error}
            data-testid="estimates-error-alert"
          />
        )}

        {/* Success alert */}
        {uploadData && (
          <Alert
            type="success"
            showIcon
            closable
            onClose={reset}
            className={styles.alert}
            message="Upload Completed"
            data-testid="estimates-success-alert"
            description={
              <span>
                <strong>{uploadData.inserted.toLocaleString()}</strong> records inserted for{' '}
                <strong>{uploadData.monthYear}</strong>.
                {uploadData.skipped > 0 && (
                  <span>
                    {' '}
                    <strong style={{ color: '#d97706' }}>{uploadData.skipped}</strong> row
                    {uploadData.skipped !== 1 ? 's' : ''} skipped (missing location or meter not
                    found).
                  </span>
                )}
              </span>
            }
          />
        )}

        {/* Select File button */}
        <Upload
          accept=".xlsx,.xls,.xlsm"
          fileList={fileList}
          beforeUpload={() => false}
          onChange={handleChange}
          maxCount={1}
          onRemove={() => {
            setFileList([]);
            if (result || error) reset();
          }}
          showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
          data-testid="file-upload"
        >
          <NSPButton icon={<UploadOutlined />} disabled={isBusy} data-testid="select-file-button">
            Select File
          </NSPButton>
        </Upload>

        {/* Progress bar */}
        {showProgress && (
          <Card
            size="small"
            className={styles.progressCard}
            data-testid="upload-progress"
            data-status={progressStatus}
            extra={
              canClose && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCloseProgress}
                  data-testid="progress-close-button"
                />
              )
            }
          >
            <Progress
              percent={progressPercent}
              status={toAntdStatus(progressStatus)}
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              data-testid="upload-progress-bar"
              data-percent={String(progressPercent)}
            />
            <div className={styles.progressMessage} data-testid="progress-message">{progressMessage}</div>
          </Card>
        )}

        {/* Action bar */}
        <div className={styles.actionBar}>
          <NSPButton
            onClick={handleUpload}
            disabled={fileList.length === 0 || isBusy}
            loading={isBusy}
            data-testid="upload-button"
          >
            {isBusy ? 'Uploading...' : 'Upload'}
          </NSPButton>
        </div>
      </div>

      {/* ── Results card (shown after a successful upload) ─────────── */}
      {uploadData && (
        <Card
          size="small"
          className={styles.statsCard}
          data-testid="estimates-results-card"
          title={<span className={styles.statsTitle}>Upload Results</span>}
        >
          <div className={styles.statsGrid}>
            <div className={styles.statItem} data-testid="estimates-stat" data-stat="month-year">
              <div className={styles.statLabel}>Month-Year</div>
              <div className={styles.statValue}>{uploadData.monthYear}</div>
            </div>
            <div className={styles.statItem} data-testid="estimates-stat" data-stat="total-rows">
              <div className={styles.statLabel}>Total Rows</div>
              <div className={styles.statValue}>{uploadData.totalRows.toLocaleString()}</div>
            </div>
            <div className={styles.statItem} data-testid="estimates-stat" data-stat="records-inserted">
              <div className={styles.statLabel}>Records Inserted</div>
              <div className={styles.statValue}>{uploadData.inserted.toLocaleString()}</div>
            </div>
            <div className={styles.statItem} data-testid="estimates-stat" data-stat="rows-skipped">
              <div className={styles.statLabel}>Rows Skipped</div>
              <div className={uploadData.skipped > 0 ? styles.statValueWarning : styles.statValue}>
                {uploadData.skipped}
              </div>
            </div>
          </div>

          {/* Skipped rows detail table */}
          {uploadData.skipped > 0 && (
            <div className={styles.unmatchedBlock} data-testid="skipped-rows-block">
              <div className={styles.unmatchedHeader}>
                The following rows were skipped and not inserted:
              </div>
              <Table<SkippedRow>
                dataSource={uploadData.skippedRows}
                columns={withTestColumnAttrs(skippedColumns, 'skipped-row-value')}
                rowKey="rowNumber"
                size="small"
                pagination={false}
                className={styles.unmatchedTable}
                data-testid="skipped-rows-table"
                onRow={(row) =>
                  ({
                    'data-testid': 'skipped-row',
                    'data-row-number': String(row.rowNumber),
                    'data-meter-number': row.meterNumber ?? '',
                  }) as TestAttrs
                }
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
